import {
  Flex,
  Icon,
  InputText,
  RadioSelectionBlock,
  Text,
  Tooltip,
} from '@driftt/dds'
import { faCircleInfo } from '@driftt/dds.icons/light/faCircleInfo'
import React, { useEffect } from 'react'

import { useUrlInput } from '../hooks'

const RedirectAbilityStatus = {
  redirect: 'redirect',
  noRedirect: 'noRedirect',
} as const

export interface FLFormRedirectUrlInputProps {
  onChange: ({
    redirectUrl,
    hasRedirect,
    urlError,
  }: {
    redirectUrl?: string
    hasRedirect: boolean
    urlError?: string
  }) => void
  redirectUrl?: string
  hasRedirect?: boolean
  disabled?: boolean
}

export const FLFormRedirectUrlInput = ({
  onChange,
  redirectUrl,
  hasRedirect,
  disabled,
}: FLFormRedirectUrlInputProps) => {
  const {
    handleUrlInputBlur,
    error: urlError,
    handleUrlInputChange,
  } = useUrlInput({
    onChange: (newVal) => {
      onChange({
        redirectUrl: newVal,
        hasRedirect: true,
        urlError: urlError,
      })
    },
  })

  const handleSelectionChange = (newVal: string) => {
    if (newVal === RedirectAbilityStatus.redirect) {
      onChange({
        redirectUrl,
        hasRedirect: true,
        urlError,
      })
    } else {
      onChange({
        redirectUrl: undefined,
        hasRedirect: false,
        urlError,
      })
    }
  }

  // Use Foot 🔫 Alert: We need to do this because the urlError is not
  // controlled by this component, but we still want to pass it up to
  // the parent through the onChange prop when it changes.
  useEffect(() => {
    onChange({
      redirectUrl,
      hasRedirect: !!hasRedirect,
      urlError,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlError])

  return (
    <Flex direction="column">
      <Flex gap="half" alignItems="center">
        <Text>
          After submitting this form, are visitors redirected to a different
          page?
        </Text>
        <Tooltip content="Unsure if your form goes to a new page after submission? Try submitting the form as a test — if you land on a different page after submitting, you have a redirect and will want to select “Yes” and include the URL you landed on.">
          <Icon icon={faCircleInfo} />
        </Tooltip>
      </Flex>
      <RadioSelectionBlock.Group
        onChange={handleSelectionChange}
        {...(hasRedirect != null && {
          value: hasRedirect
            ? RedirectAbilityStatus.redirect
            : RedirectAbilityStatus.noRedirect,
        })}
      >
        <RadioSelectionBlock.Item
          // Issue with DDS RadioSelectionBlock.Item causes the need for || undefined
          disabled={disabled || undefined}
          header="Yes, visitors are redirected to a new page after submitting this form"
          value={RedirectAbilityStatus.redirect}
        >
          <InputText
            disabled={disabled}
            size="full-width"
            label="Follow-up URL"
            placeholder="Enter a follow-up URL"
            description="Site visitors will be redirected to this URL after they interact with Fastlane."
            value={redirectUrl}
            onChange={handleUrlInputChange}
            errorMessage={disabled ? '' : urlError}
            onBlur={handleUrlInputBlur}
          />
        </RadioSelectionBlock.Item>
        <RadioSelectionBlock.Item
          // Issue with DDS RadioSelectionBlock.Item causes the need for || undefined
          disabled={disabled || undefined}
          header="No, visitors stay on this page after submitting the form"
          value={RedirectAbilityStatus.noRedirect}
        />
      </RadioSelectionBlock.Group>
    </Flex>
  )
}
